import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { useDialog } from "../../../DialogContext";
import {
  getCases,
  getPracticeArea,
} from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../../static/server";
import {
  getAllAccounts,
  getAllStatement,
} from "../../../../redux/Apps/Lawyerapp/AccountSlice/AccountSlice";
import { getAllCustomField } from "../../../../redux/Apps/Lawyerapp/CustomFieldSlice";
import {
  handleMatterNewTab,
  ListboxComponent,
  NoDataOptionField,
} from "../../../../static/staticdata";
import {
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../../styles/styles";
import noOption from "../../../../assets/no-option-found.webp";
import ContactApiField from "../../Settings/FieldsUi/ContactApiField";
import { getCurrencySymbol } from "../../../../pages/LawyerPages/Settings/CurrencySetForm";
import {
  buildCurrencyOptions,
  isCurrencyConfigured,
} from "../../../../utils/currencyConfig";

const TranferFundFrom = ({ accountId }) => {
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    token,
    teamMemberLawyerId,
    defaultCurrency,
    UserCurrencyList = [],
  } = useSelector((data) => data.user);
  const { allCase } = useSelector((state) => state.case);
  const { AllAccounts } = useSelector((state) => state.account);

  useEffect(() => {
    dispatch(
      getCases({
        url: `${backend_api}case/register/`,
        token: token,
        teamMemberLawyerId,
      })
    );
    dispatch(
      getAllAccounts({ url: `${backend_api}bank/accounts/`, token: token })
    );
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      })
    );
    dispatch(
      getAllCustomField({
        url: `${backend_api}custom-field/?field_for=case`,
        token: token,
      })
    );
  }, [dispatch, teamMemberLawyerId, token]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      between_to: "bank_ac",
      date: dayjs().format("YYYY-MM-DD"),
      currency: "",
    },
  });

  const selectedSourceAccountId = watch("source_account");
  const selectedDestinationAccountId = watch("destination_account");
  const selectedCurrencyValue = watch("currency");

  const selectedSourceAccount = useMemo(
    () =>
      AllAccounts?.find((account) => account.id === selectedSourceAccountId) ||
      null,
    [AllAccounts, selectedSourceAccountId],
  );

  const selectedDestinationAccount = useMemo(
    () =>
      AllAccounts?.find(
        (account) => account.id === selectedDestinationAccountId,
      ) || null,
    [AllAccounts, selectedDestinationAccountId],
  );

  const transferCurrency =
    selectedSourceAccount?.currency ||
    selectedCurrencyValue ||
    defaultCurrency ||
    "USD";
  const currencySymbol = getCurrencySymbol(transferCurrency) || transferCurrency;

  const currencyOptions = useMemo(
    () =>
      buildCurrencyOptions({
        userCurrencies: UserCurrencyList,
        includeValue: transferCurrency,
      }),
    [UserCurrencyList, transferCurrency],
  );

  const sourceAccountOptions = useMemo(
    () =>
      (AllAccounts || []).filter(
        (account) => account?.id !== selectedDestinationAccountId,
      ),
    [AllAccounts, selectedDestinationAccountId],
  );

  const destinationAccountOptions = useMemo(
    () =>
      (AllAccounts || []).filter((account) => {
        if (account?.id === selectedSourceAccountId) {
          return false;
        }

        if (!selectedSourceAccount?.currency) {
          return true;
        }

        return account?.currency === selectedSourceAccount.currency;
      }),
    [AllAccounts, selectedSourceAccountId, selectedSourceAccount?.currency],
  );

  const isSourceCurrencySynced =
    !selectedSourceAccount?.currency ||
    !Array.isArray(UserCurrencyList) ||
    UserCurrencyList.length === 0 ||
    isCurrencyConfigured(UserCurrencyList, selectedSourceAccount.currency);

  useEffect(() => {
    if (!accountId || !AllAccounts?.length) return;

    const defaultSourceAccount =
      AllAccounts.find((account) => account.id === Number(accountId)) || null;

    if (defaultSourceAccount?.id) {
      setValue("source_account", defaultSourceAccount.id, {
        shouldValidate: true,
      });
    }
  }, [accountId, AllAccounts, setValue]);

  useEffect(() => {
    if (!selectedSourceAccount?.currency) return;

    if (selectedCurrencyValue !== selectedSourceAccount.currency) {
      setValue("currency", selectedSourceAccount.currency, {
        shouldValidate: true,
      });
    }

    if (
      selectedDestinationAccount &&
      selectedDestinationAccount.currency !== selectedSourceAccount.currency
    ) {
      setValue("destination_account", null, { shouldValidate: true });
    }
  }, [
    selectedCurrencyValue,
    selectedDestinationAccount,
    selectedSourceAccount,
    setValue,
  ]);

  const handleOpenBillingPreferences = () => {
    closeDialog();
    navigate("/dashboard/settings/billing-preferences");
  };

  const handleOpenBankAccounts = () => {
    closeDialog();
    navigate("/dashboard/bank_accounts");
  };
  const onSubmit = async (data) => {
    if (!selectedSourceAccount || !selectedDestinationAccount) {
      toast.error("Select both source and destination accounts");
      return;
    }

    if (selectedSourceAccount.currency !== selectedDestinationAccount.currency) {
      toast.error("Source and destination accounts must use the same currency");
      return;
    }

    const sourceWithdrawData = {
      transaction_type: "withdrawal",
      account: data?.source_account,
      date: data?.date,
      client: data?.client,
      matter: data?.matter,
      source_account: data?.source_account,
      destination_account: data?.destination_account,
      payer_payee: data?.payer_payee,
      currency: transferCurrency,
      amount: data?.amount,
      exchange_rate: data?.exchange_rate,
      description: data?.description,
      status: "completed",
    };

    const destinationDepositData = {
      transaction_type: "deposit",
      account: data?.destination_account,
      date: data?.date,
      client: data?.client,
      matter: data?.matter,
      source_account: data?.source_account,
      destination_account: data?.destination_account,
      payer_payee: data?.payer_payee,
      currency: transferCurrency,
      amount: data?.amount,
      exchange_rate: data?.exchange_rate,
      description: data?.description,
      status: "completed",
    };

    try {
      await Promise.all([
        axios.post(`${backend_api}bank/transactions/`, sourceWithdrawData, {
          headers: { Authorization: `Token ${token}` },
        }),
        axios.post(`${backend_api}bank/transactions/`, destinationDepositData, {
          headers: { Authorization: `Token ${token}` },
        }),
      ]);

      toast.success("Funds transferred successfully");
      dispatch(
        getAllStatement({
          url: `${backend_api}bank/transactions/?account=${accountId}`,
          token: token,
        })
      );
      reset();
      closeDialog();
    } catch (error) {
      toast.error("Transaction failed. Please try again.");
    }
  };

  return (
    <>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              p: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            {selectedSourceAccount?.currency &&
              (!isSourceCurrencySynced ||
                destinationAccountOptions.length === 0) && (
                <Alert
                  severity="warning"
                  action={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {!isSourceCurrencySynced && (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleOpenBillingPreferences}
                        >
                          Billing Preferences
                        </Button>
                      )}
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleOpenBankAccounts}
                      >
                        Bank Accounts
                      </Button>
                    </Box>
                  }
                >
                  {!isSourceCurrencySynced
                    ? `The source account uses ${selectedSourceAccount.currency}, but that currency is not active in Billing Preferences yet.`
                    : `No destination accounts are available in ${selectedSourceAccount.currency}. Create another account in the same currency to transfer funds.`}
                </Alert>
              )}
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="between_to"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel
                      id="between_to-label"
                      sx={{ bgcolor: "#fff", px: "0.3rem" }}
                    >
                      Between to
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="between_to-label"
                      defaultValue="bank_ac"
                    >
                      <MenuItem value={"bank_ac"}>Bank Accounts</MenuItem>
                      <MenuItem disabled value={"matters"}>
                        Matters
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <ContactApiField
                name="client"
                label="Client"
                control={control}
                errors={errors}
                token={token}
                apiUrl={`${backend_api}contact/retrieve/`}
                isRequired
              />
            </Box>
            <Box>
              <Controller
                name="date"
                control={control}
                rules={{ required: "Date is required" }}
                render={({ field, value }) => (
                  <DatePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      const isValidDate = newValue && dayjs(newValue).isValid();
                      field.onChange(
                        isValidDate
                          ? dayjs(newValue).startOf("day").format("YYYY-MM-DD")
                          : null
                      );
                    }}
                    sx={{
                      width: "100%",
                    }}
                    slotProps={{
                      textField: {
                        label: (
                          <span>
                            Date<span style={{ color: "red" }}>*</span>
                          </span>
                        ),
                        size: "small",
                        variant: "outlined",
                        fullWidth: true,
                      },
                      popper: {
                        sx: {
                          "& .MuiPickersPopper-root": {
                            maxWidth: "100%",
                            maxHeight: "100%",
                            overflow: "hidden",
                          },
                          "& .MuiPaper-root": {
                            width: "fit-content",
                            maxHeight: "15rem",
                            overflowY: "auto",
                            ...pageScrollStyle,
                          },
                        },
                      },
                    }}
                    format="MM/DD/YYYY"
                  />
                )}
              />
            </Box>

            <Controller
              name="matter"
              control={control}
              rules={{ required: "Matter is required" }}
              render={({ field }) => (
                <FormControl size="small" fullWidth error={errors?.matter}>
                  <Autocomplete
                    {...field}
                    options={allCase || []}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      return generateUniqueMatterName(
                        option.id,
                        option.case_name
                      );
                    }}
                    value={
                      field.value
                        ? allCase?.find((item) => item.id === field.value)
                        : null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue?.id || null)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Matter" size="small" />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          <Typography>
                            {option.id
                              ? `000${option.id}-${option.case_name}`
                              : ""}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              p: "0.2rem 0.5rem",
                              borderRadius: "1rem",
                              textAlign: "center",
                              fontSize: "0.7rem",
                              bgcolor:
                                option?.case_status === "open"
                                  ? "#4CAF50"
                                  : option?.case_status === "pending"
                                  ? "#FFC107"
                                  : "#F44336",
                              color: "#fff",
                            }}
                          >
                            {option?.case_status}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    ListboxComponent={(props) => (
                      <ListboxComponent
                        {...props}
                        btnIcon={<ArrowOutwardRoundedIcon />}
                        btnText="Add Matter"
                        handleClikListBtn={handleMatterNewTab}
                      />
                    )}
                    noOptionsText={
                      <NoDataOptionField
                        imgSrc={noOption}
                        text="No matters found"
                        btnText="Matter"
                        handleClickNoData={() => navigate("/dashboard/matter")}
                      />
                    }
                  />
                </FormControl>
              )}
            />
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="source_account"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Autocomplete
                      disablePortal
                      options={sourceAccountOptions}
                      getOptionLabel={(option) => option.account_name || ""}
                      value={
                        AllAccounts?.find((acc) => acc.id === field.value) ||
                        null
                      }
                      onChange={(_, selectedAccount) =>
                        field.onChange(
                          selectedAccount ? selectedAccount.id : null
                        )
                      }
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1">
                            {option.account_name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {option.currency || "-"}
                            </Typography>
                            {option.is_default && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            <span>
                              Source Account
                              <span style={{ color: "red" }}>*</span>
                            </span>
                          }
                          size="small"
                        />
                      )}
                    />
                  </FormControl>
                )}
              />

              <Controller
                name="destination_account"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Autocomplete
                      disablePortal
                      options={destinationAccountOptions}
                      getOptionLabel={(option) => option.account_name || ""}
                      value={
                        AllAccounts?.find((acc) => acc.id === field.value) ||
                        null
                      }
                      onChange={(_, selectedAccount) =>
                        field.onChange(
                          selectedAccount ? selectedAccount.id : null
                        )
                      }
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1">
                            {option.account_name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {option.currency || "-"}
                            </Typography>
                            {option.is_default && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            <span>
                              Destination Account
                              <span style={{ color: "red" }}>*</span>
                            </span>
                          }
                          size="small"
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={currencyOptions}
                      getOptionLabel={(option) => option?.label || ""}
                      value={
                        currencyOptions.find(
                          (option) => option?.value === field?.value
                        ) || null
                      }
                      onChange={(_, newValue) =>
                        field.onChange(newValue ? newValue?.value : null)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      disabled={Boolean(selectedSourceAccount?.currency)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Currency"
                          size="small"
                          fullWidth
                          helperText={
                            selectedSourceAccount?.currency
                              ? "Currency follows the selected source account."
                              : ""
                          }
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: "This field is required",
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: "Only numeric values allowed",
                  },
                  validate: (value) => {
                    const numericValue = parseFloat(value);
                    if (isNaN(numericValue)) return "Invalid amount";

                    const sourceAccount = AllAccounts?.find(
                      (acc) => acc.id === watch("source_account")
                    );
                    const availableBalance = sourceAccount?.balance ?? 0;

                    if (numericValue > availableBalance) {
                      return `Insufficient funds. Available: ${currencySymbol} ${availableBalance}`;
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label={
                      <span>
                        Amount
                        <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    inputProps={{
                      inputMode: "decimal",
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9.]/g, "");
                      if (e.target.value.split(".").length > 2) {
                        e.target.value = e.target.value.slice(0, -1);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: "6px",
                              backgroundColor: "white",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            {currencySymbol}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="exchange_rate" //this input use for the set the exchange rate of two diff currency rate inr to usd
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Exchange Rate"
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="payer_payee"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Payer/Payee"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Box>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={1.5}
                  fullWidth
                  label="Description"
                />
              )}
            />
          </Box>
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => closeDialog()}
            >
              Cancel
            </Button>
            <LoadingButton
              //   loading={submitLoading}
              variant="contained"
              color="primary"
              type="submit"
              size="small"
            >
              Record transfer fund
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default TranferFundFrom;
